import { useEffect, useState } from "react";
import { Loader2, Check, AlertCircle, Wand2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Required-aspects assistant — pulls eBay's mandatory specifics for the
 * current category and lets the user fill them in via searchable popovers.
 * Extracted verbatim from DraftCreator.jsx.
 */
export default function AspectsAssist({ aspects, loading, categoryId, specifics, onFill }) {
  if (!categoryId) {
    return (
      <p className="mb-2 text-[11px] text-slate-400">
        Paste a numeric eBay category ID above to pull required item specifics from eBay.
      </p>
    );
  }
  if (loading) {
    return (
      <div className="mb-2 flex items-center gap-1 text-[11px] text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Fetching required specifics from eBay…
      </div>
    );
  }
  if (!aspects || !aspects.aspects) return null;

  const specificsByKey = new Map(
    (specifics || []).map((s) => [(s.key || "").toLowerCase(), s.value || ""])
  );
  const required = aspects.aspects.filter((a) => a.required);
  const missing = required.filter((a) => !(specificsByKey.get(a.name.toLowerCase()) || "").trim());

  const fillMissing = () => {
    const existing = new Map((specifics || []).map((s) => [(s.key || "").toLowerCase(), s]));
    for (const a of missing) {
      const key = a.name.toLowerCase();
      if (existing.has(key)) continue;
      existing.set(key, { key: a.name, value: "" });
    }
    const originalKeys = new Set((specifics || []).map((s) => (s.key || "").toLowerCase()));
    const merged = [...(specifics || [])];
    for (const a of missing) {
      if (!originalKeys.has(a.name.toLowerCase())) merged.push({ key: a.name, value: "" });
    }
    onFill(merged);
  };

  if (required.length === 0) {
    return (
      <p className="mb-2 text-[11px] text-emerald-700" data-testid="aspects-none-required">
        <Check className="mr-1 inline h-3 w-3" /> eBay lists no mandatory specifics for category {categoryId}.
      </p>
    );
  }

  return (
    <div
      className={`mb-3 rounded-md border p-3 text-xs ${
        missing.length > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"
      }`}
      data-testid="aspects-assist"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 font-semibold">
          {missing.length > 0 ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-amber-700" />
              <span className="text-amber-900">
                {missing.length} required specific{missing.length > 1 ? "s" : ""} missing
              </span>
            </>
          ) : (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-700" />
              <span className="text-emerald-900">All required specifics filled</span>
            </>
          )}
        </div>
        {missing.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={fillMissing}
            className="h-6 rounded-full border-amber-400 text-[11px] text-amber-800 hover:bg-amber-100"
            data-testid="fill-required-aspects-button"
          >
            <Wand2 className="mr-1 h-3 w-3" /> Add {missing.length}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {required.map((a) => {
          const currentValue = specificsByKey.get(a.name.toLowerCase()) || "";
          return (
            <AspectChip
              key={a.name}
              aspect={a}
              currentValue={currentValue}
              onSet={(newValue) => {
                const trimmed = (newValue || "").trim();
                const specs = specifics || [];
                const idx = specs.findIndex((s) => (s.key || "").toLowerCase() === a.name.toLowerCase());
                if (idx >= 0) {
                  if (!trimmed) {
                    const next = [...specs];
                    next[idx] = { ...next[idx], value: "" };
                    onFill(next);
                  } else {
                    const next = [...specs];
                    next[idx] = { ...next[idx], value: trimmed };
                    onFill(next);
                  }
                } else if (trimmed) {
                  onFill([...specs, { key: a.name, value: trimmed }]);
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function AspectChip({ aspect, currentValue, onSet }) {
  const [open, setOpen] = useState(false);
  const [freeText, setFreeText] = useState(currentValue || "");
  const [search, setSearch] = useState("");
  const filled = (currentValue || "").trim().length > 0;
  const hasValues = Array.isArray(aspect.values) && aspect.values.length > 0;

  useEffect(() => { setFreeText(currentValue || ""); }, [currentValue, open]);

  const filtered = hasValues
    ? aspect.values.filter((v) => v.toLowerCase().includes((search || "").toLowerCase()))
    : [];

  const commit = (v) => {
    onSet(v);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`required-aspect-${aspect.name}`}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider transition-colors ${
            filled
              ? "border-emerald-300 bg-emerald-100 text-emerald-800 hover:border-emerald-400 hover:bg-emerald-200"
              : "border-amber-300 bg-white text-amber-800 hover:border-amber-400 hover:bg-amber-100"
          }`}
        >
          {filled ? <Check className="h-2.5 w-2.5" /> : <AlertCircle className="h-2.5 w-2.5" />}
          {aspect.name}
          {filled && (
            <span className="ml-1 max-w-[90px] truncate rounded-sm bg-emerald-200 px-1 font-sans text-[9px] normal-case tracking-normal text-emerald-900">
              {currentValue}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" data-testid={`aspect-popover-${aspect.name}`}>
        <p className="mb-1.5 flex items-center justify-between text-[11px] font-semibold text-slate-800">
          <span>{aspect.name}</span>
          {aspect.mode === "SELECTION_ONLY" && (
            <span className="rounded bg-amber-100 px-1 text-[9px] font-mono uppercase text-amber-800">Select only</span>
          )}
        </p>
        {hasValues && (
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter…"
            className="mb-2 h-7 text-xs"
            data-testid={`aspect-search-${aspect.name}`}
            autoFocus
          />
        )}
        {hasValues ? (
          <div className="max-h-56 space-y-0.5 overflow-y-auto pr-1">
            {filtered.slice(0, 100).map((v) => {
              const isCurrent = v === currentValue;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => commit(v)}
                  className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs transition-colors ${
                    isCurrent
                      ? "bg-emerald-50 text-emerald-800"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                  data-testid={`aspect-value-${aspect.name}-${v}`}
                >
                  <span className="truncate">{v}</span>
                  {isCurrent && <Check className="h-3 w-3 text-emerald-600" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-2 py-1 text-[11px] text-slate-500">No matches — type below to enter a custom value.</p>
            )}
            {aspect.mode !== "SELECTION_ONLY" && (
              <div className="mt-2 border-t border-slate-100 pt-2">
                <p className="mb-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">Or type your own</p>
                <div className="flex gap-1">
                  <Input
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(freeText); } }}
                    placeholder={`Custom ${aspect.name.toLowerCase()}…`}
                    className="h-7 text-xs"
                    data-testid={`aspect-freetext-${aspect.name}`}
                  />
                  <Button size="sm" onClick={() => commit(freeText)} className="h-7 rounded bg-slate-900 px-2 text-xs text-white hover:bg-slate-800" data-testid={`aspect-save-${aspect.name}`}>
                    Set
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="mb-1 text-[11px] text-slate-500">
              eBay doesn't provide suggested values — type your own.
            </p>
            <div className="flex gap-1">
              <Input
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(freeText); } }}
                placeholder={`Enter ${aspect.name.toLowerCase()}…`}
                className="h-7 text-xs"
                data-testid={`aspect-freetext-${aspect.name}`}
                autoFocus
              />
              <Button size="sm" onClick={() => commit(freeText)} className="h-7 rounded bg-slate-900 px-2 text-xs text-white hover:bg-slate-800" data-testid={`aspect-save-${aspect.name}`}>
                Set
              </Button>
            </div>
          </div>
        )}
        {filled && (
          <button
            type="button"
            onClick={() => commit("")}
            className="mt-2 w-full rounded border border-slate-200 py-0.5 text-[10px] text-slate-500 hover:bg-slate-50"
            data-testid={`aspect-clear-${aspect.name}`}
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
